/**
 * Estado global de la interfaz: datos locales, contexto temporal y preferencias del dispositivo.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMeta, setMeta } from './db/db';
import type { Ctx } from './domain/actions';
import { indexData, type HabitIndex } from './domain/selectors';
import { useAppData, useCtx, type AppData } from './domain/useData';
import { detectLocale, type Locale } from './i18n';

export type Theme = 'system' | 'light' | 'dark';

interface AppState {
  data: AppData;
  ctx: Ctx | null;
  idx: HabitIndex;
  locale: Locale;
  setLocale: (l: Locale) => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const AppContext = createContext<AppState | null>(null);

export function usePrefs() {
  const [locale, setLocaleState] = useState<Locale | null>(null);
  const [theme, setThemeState] = useState<Theme>('system');
  useEffect(() => {
    void (async () => {
      setLocaleState(await getMeta<Locale>('locale', detectLocale()));
      setThemeState(await getMeta<Theme>('theme', 'system'));
    })();
  }, []);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);
  useEffect(() => {
    if (locale) document.documentElement.lang = locale;
  }, [locale]);
  return {
    locale,
    theme,
    setLocale: (l: Locale) => {
      setLocaleState(l);
      void setMeta('locale', l);
    },
    setTheme: (t: Theme) => {
      setThemeState(t);
      void setMeta('theme', t);
    },
  };
}

export function AppStateProvider({
  data,
  prefs,
  children,
}: {
  data: AppData;
  prefs: { locale: Locale; theme: Theme; setLocale: (l: Locale) => void; setTheme: (t: Theme) => void };
  children: ReactNode;
}) {
  const ctx = useCtx(data.settings);
  const idx = useMemo(() => indexData(data), [data]);
  const value = useMemo<AppState>(() => ({ data, ctx, idx, ...prefs }), [data, ctx, idx, prefs]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppState & { ctx: Ctx } {
  const v = useContext(AppContext);
  if (!v || !v.ctx) throw new Error('Estado de la app no disponible');
  return v as AppState & { ctx: Ctx };
}

export { useAppData };
